/******************************************************************************
 * Copyright (c) 2026 Contributors to the Eclipse Foundation.
 *
 * See the NOTICE file(s) distributed with this work for additional
 * information regarding copyright ownership.
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * https://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *****************************************************************************/
import { useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MainContext } from '../../context';
import { useReportedQuery } from '../../hooks/use-reported-query';
import { extensionDetailKeys } from './use-extension-details';
const reviewsKey = (extension) => ['extension-reviews', extension.namespace, extension.name];
export const useExtensionReviews = (extension) => {
    const { service } = useContext(MainContext);
    return useReportedQuery(useQuery({
        queryKey: reviewsKey(extension),
        queryFn: ({ signal }) => service.getExtensionReviews(signal, extension)
    }), { action: 'load the reviews' });
};
// A review changes the list and the rating and review count in the page header. Returned to
// `onSuccess`, so a mutation stays pending until both have been refetched.
const useRefreshReviews = (extension) => {
    const queryClient = useQueryClient();
    return () => Promise.all([
        queryClient.invalidateQueries({ queryKey: reviewsKey(extension) }),
        queryClient.invalidateQueries({
            queryKey: extensionDetailKeys.all(extension.namespace, extension.name)
        })
    ]);
};
export const usePostReview = (extension) => {
    const { service } = useContext(MainContext);
    const refreshReviews = useRefreshReviews(extension);
    return useReportedQuery(useMutation({
        mutationFn: ({ review, postUrl }) => service.postReview(review, postUrl),
        onSuccess: refreshReviews
    }), { action: 'post your review' });
};
/** Revokes the current user's own review. */
export const useDeleteReview = (extension) => {
    const { service } = useContext(MainContext);
    const refreshReviews = useRefreshReviews(extension);
    return useReportedQuery(useMutation({
        mutationFn: (deleteUrl) => service.deleteReview(deleteUrl),
        onSuccess: refreshReviews
    }), { action: 'remove your review' });
};
/** Removes another user's review, as a moderator. */
export const useDeleteUserReview = (extension) => {
    const { service } = useContext(MainContext);
    const refreshReviews = useRefreshReviews(extension);
    return useReportedQuery(useMutation({
        mutationFn: (user) => service.deleteUserReview(extension, user),
        onSuccess: refreshReviews
    }), { action: 'remove the review' });
};
//# sourceMappingURL=use-extension-reviews.js.map